import React from "react";

import {
    PAPER_INITIAL_CAPITAL_KRW,
    CAPITAL_BASELINE_PCT,
    computeCapital,
} from "../../data/paperCapital";

/**
 * CapitalStat — 전략 1종(또는 그룹 합산)의 가상 자본 현황 미니 시각화.
 *
 * "시작 자본 → 현재 남은 자본 + 수익률" 을 카드 안에 한 블록으로 보여준다.
 * 자본 바는 중앙 세로선(= 시작 자본)을 기준으로, 채움이 중앙을 넘으면 이익(gain),
 * 못 미치면 손실(loss) 색으로 칠해져 "지금 본전 위/아래인지" 를 한눈에 읽힌다.
 *
 * @param {number} pnlKrw 실현 손익 (KRW).
 * @param {number} [units=1] 합산 종 수 (그룹 카드면 멤버 수). 시작 자본 = units × 기본.
 * @param {number} [initialCapital] 시작 자본 직접 지정 (전략별 예외용). units 보다 우선.
 */
export default function CapitalStat({ pnlKrw, units = 1, initialCapital }) {
    const base =
        initialCapital != null
            ? initialCapital
            : PAPER_INITIAL_CAPITAL_KRW * Math.max(1, units);
    const { initial, current, pnl, returnPct, fillPct } = computeCapital(pnlKrw, base);

    const tone = pnl > 0 ? "gain" : pnl < 0 ? "loss" : "neutral";
    const valueClass =
        tone === "gain" ? "text-gain" : tone === "loss" ? "text-loss" : "text-content";
    const accentClass =
        tone === "gain"
            ? "text-gain"
            : tone === "loss"
              ? "text-loss"
              : "text-content-secondary";
    const barClass =
        tone === "gain" ? "bg-gain" : tone === "loss" ? "bg-loss" : "bg-content-muted";

    const aria = `시작 자본 ${krw(initial)}, 현재 남은 자본 ${krw(current)}, 수익률 ${signed(
        returnPct,
        (v) => `${v.toFixed(1)}%`,
    )}`;

    return (
        <div className="mt-2 mb-2">
            <div className="flex items-baseline justify-between gap-2">
                <span className="text-xs text-content-muted">
                    남은 자본
                    {units > 1 && (
                        <span className="ml-1 text-content-muted">· {units}종 합산</span>
                    )}
                </span>
                <span className={`text-xs font-semibold tabular-nums ${accentClass}`}>
                    {signed(returnPct, (v) => `${v.toFixed(1)}%`)}
                </span>
            </div>
            <div className={`text-base font-bold tabular-nums leading-tight ${valueClass}`}>
                {krw(current)}
            </div>
            <div
                className="relative mt-1.5 h-1.5 rounded-full bg-surface-elevated overflow-hidden"
                role="img"
                aria-label={aria}
            >
                {/* 채움 — 0% 에서 현재 자본 위치까지 (중앙 = 시작 자본) */}
                <div
                    className={`absolute inset-y-0 left-0 ${barClass} transition-[width] duration-300`}
                    style={{ width: `${fillPct}%` }}
                />
                {/* 시작 자본 기준선 — 중앙 세로선 */}
                <div
                    aria-hidden="true"
                    className="absolute inset-y-0 w-px bg-surface-card"
                    style={{ left: `${CAPITAL_BASELINE_PCT}%` }}
                />
            </div>
            <div className="mt-1 text-xs text-content-muted tabular-nums">
                시작 자본 {krw(initial)}
            </div>
        </div>
    );
}

/** KRW 정수 표기 — ₩10,240,000. */
function krw(v) {
    return `₩${Math.round(v).toLocaleString()}`;
}

/** 부호(+/-)를 앞에 붙여 포맷. 0 은 부호 없음. */
function signed(v, fmt) {
    const sign = v > 0 ? "+" : v < 0 ? "-" : "";
    return `${sign}${fmt(Math.abs(v))}`;
}
